import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { produce } from "immer";
import moment from "moment";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import KanbanTaskCard from "../../../Components/Cards/KanbanTaskCard/kanban-task-card";
import ListPageHeader, {
  ListPageHeaderGroupActions,
  ListPageHeaderRight,
} from "../../../Components/Misc/ListPageHeader";
import { EventNotifierOperation } from "../../../Core/Api/notifier-client";
import {
  activitiesNotifier,
  cacheService,
  calendarNotifier,
  planningUserTaskService,
  userService,
} from "../../../Core/Api/services";
import { DbOrderManualTaskPlanningInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-infos";
import { ManualTaskPlanningStatus } from "../../../Core/Api/Types/manual-task-planning-status";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";

import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import SearchIcon from "@mui/icons-material/Search";
import UserTaskPlanningPanel from "../../../Components/Panels/UserTaskPlanningPanel/user-task-planning-panel";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { Distinct, MoveInArray } from "../../../Utils/helpers";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { GroupPlanningInfos } from "../../../Core/Api/Types/group-planning-infos";
import { DbActivityInfos } from "../../../Core/Api/Types/db-activity-infos";
import { useTranslation } from "react-i18next";

interface TaskColumnGroup {
  status: ManualTaskPlanningStatus;
  tasks: DbOrderManualTaskPlanningInfos[];
}

function PlanningKanban() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<DbOrderManualTaskPlanningInfos[]>([]);
  const [users, setUsers] = useState<DbUserBasicInfos[]>([]);
  const [resources, setResources] = useState<DbOrderManualTaskPlanningResourceInfos[]>([]);
  const [taskGroups, setTaskGroups] = useState<TaskColumnGroup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterService, setFilterService] = useState("");
  const [filterTask, setFilterTask] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filterDate, setFilterDate] = useState<"" | "Today" | "Tomorrow" | string>("");
  const [filterOrderBy, setFilterOrderBy] = useState<"" | "DueDate" | "CreationDate" | string>("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [groupPlanningInfos, setGroupPlanningInfos] = useState<GroupPlanningInfos>();

  const user = cacheService.GetUserInfo();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const theme = useTheme();
  const [selectedTaskId, setSelectedTaskId] = useState(0);
  const { active: panelOpen, toggle: togglePanelOpen } = useToggle();

  const statuses = [
    ManualTaskPlanningStatus.Created,
    ManualTaskPlanningStatus.ToDo,
    ManualTaskPlanningStatus.InProgress,
    ManualTaskPlanningStatus.Standby,
    ManualTaskPlanningStatus.Completed,
  ];

  const viewableUsers = useMemo(() => {
    if (!groupPlanningInfos || !groupPlanningInfos.viewableGroupIds?.length) return users;
    return users
      .filter((x) => x.enabled && !x.deleted && groupPlanningInfos.viewableGroupIds!.includes(x.groupId!))
      .sort((a, b) => a.name!.localeCompare(b.name!));
  }, [users, groupPlanningInfos]);

  const onDragEnd = (move: any) => {
    /*
    draggableId: any
    source: {droppableId: any, index: number}
    destination: {droppableId: any, index: number}
    */

    let srcColumn: ManualTaskPlanningStatus = move.source.droppableId as ManualTaskPlanningStatus;
    let dstColumn: ManualTaskPlanningStatus = move.destination.droppableId as ManualTaskPlanningStatus;
    let srcTask = taskGroups.find((x) => x.status === srcColumn)!.tasks[move.source.index];
    let srcTaskId = parseInt(move.draggableId.toString().replace("task", "")); // "taskXXXXX"

    console.debug(
      "kanban:: move src col = [" +
        srcColumn +
        "] from index = [" +
        move.source.index +
        "] to newIndex = [" +
        move.destination.index +
        "] to dst col = [" +
        dstColumn +
        "] with taskId = [" +
        srcTaskId +
        "]"
    );

    if (srcColumn === dstColumn)
      setTaskGroups(
        produce((draft) => {
          MoveInArray(draft.find((x) => x.status === srcColumn)!.tasks, move.source.index, move.destination.index);
        })
      );
    else {
      setTaskGroups(
        produce((draft) => {
          draft.find((x) => x.status === dstColumn)!.tasks.splice(move.destination.index, 0, srcTask);
          draft.find((x) => x.status === srcColumn)!.tasks.splice(move.source.index, 1);
        })
      );

      planningUserTaskService.MoveTaskById(srcTaskId, dstColumn).then((response) => {
        if (!response.success) enqueueCustomSnackbar(response.message ?? t("alert:error.unknowIssue"), "warning");
      });
    }
  };

  const handleOpenPanel = (taskId: number) => {
    setSelectedTaskId(taskId);
    togglePanelOpen();
  };

  // First load API
  useEffect(() => {
    const start = moment().add(-500, "d").format("YYYY-MM-DD");
    const end = moment().add(1, "d").format("YYYY-MM-DD");

    planningUserTaskService.ListTasksByDateRange(start, end).then((response) => {
      if (response.tasks) setTasks(response.tasks);
    });

    userService.GetAllUsersBasic().then((response) => {
      if (response.users) setUsers(response.users);
    });

    planningUserTaskService.GetResourceList().then((response) => {
      if (response.resources) setResources(response.resources);
    });

    planningUserTaskService.GetGroupPlanningInfos().then((response) => {
      setGroupPlanningInfos(response.planningInfos);
    });
  }, []);

  // Fired when tasks are changed
  useEffect(() => {
    let result: TaskColumnGroup[] = [];

    for (let i = 0; i < statuses.length; ++i)
      result.push({ status: statuses[i], tasks: tasks.filter((x) => x.status === statuses[i]) });

    setTaskGroups(result);
    setIsLoading(false);
  }, [tasks]);

  // Websocket
  useEffect(() => {
    // Subscribe to websocket events
    calendarNotifier.Connect().then(function () {
      console.info("::calendar websocket connected!");

      if (calendarNotifier.connection) {
        calendarNotifier.connection.on("CalendarMessage", (data) => {
          let task = data.data as DbOrderManualTaskPlanningInfos;

          switch (data.op) {
            case EventNotifierOperation.Create:
              setTasks([...tasks, task]);

              console.debug("ws::calendar task add with Id = " + task.id);
              break;
            case EventNotifierOperation.Update:
              setTasks(
                produce((draft) => {
                  let index = draft.findIndex((x) => x.id === task.id);

                  if (index !== -1) draft[index] = task;
                })
              );

              console.debug("ws::calendar task update with Id = " + task.id);
              break;
            case EventNotifierOperation.Delete:
              setTasks(
                produce((draft) => {
                  let index = draft.findIndex((x) => x.id === task.id);

                  if (index !== -1) draft.splice(index, 1);
                })
              );

              console.debug("ws::calendar task remove with Id = " + task.id);
              break;
          }
        });
      }
    });

    return function cleanup() {
      // Disconnect hub when dismounted
      calendarNotifier.Disconnect().then(function () {
        console.info("::calendar websocket disconnected");
      });
    };
  }, []);

  useEffect(() => {
    activitiesNotifier.Connect().then(() => {
      console.info("::activities websocket connected!");
      if (activitiesNotifier.connection) {
        activitiesNotifier.connection.on("ActivityMessage", (data) => {
          // Only create as we only manage comments (always created)
          if (data.op === EventNotifierOperation.Create) {
            const activity = data.data as DbActivityInfos;
            if (!activity) return;
            const taskId = activity.resId;
            if (!taskId) return;

            setTasks(
              produce((draft) => {
                const taskIndex = draft.findIndex((x) => x.id === taskId);
                draft[taskIndex].activitiesCount += 1;
              })
            );
          }
        });
      }
    });

    return () => {
      activitiesNotifier.Disconnect().then(() => {
        console.info("::activities websocket disconnected");
      });
    };
  });

  return (
    <>
      {isLoading ? (
        <CircularProgress
          sx={{
            position: "relative",
            top: "50%",
            left: "50%",
          }}
          size={50}
        />
      ) : (
        <>
          <DragDropContext onDragEnd={onDragEnd}>
            <Stack width="100%">
              <ListPageHeader
                position="fixed"
                sx={{
                  width: "-webkit-fill-available",
                  height: "75px",
                  background: theme.palette.background.default,
                  opacity: 1,
                  zIndex: 1200,
                }}
              >
                <ListPageHeaderGroupActions>
                  <Select
                    displayEmpty
                    IconComponent={ExpandMoreRoundedIcon}
                    startAdornment={<HandshakeOutlinedIcon />}
                    inputProps={{ "aria-label": "Without label" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    value={filterCustomer}
                    onChange={(value) => setFilterCustomer(value.target.value)}
                  >
                    <MenuItem value={""}>{t("common:customer")}</MenuItem>
                    {tasks
                      .map((item) => item.customerName)
                      .filter(Distinct)
                      .map((item, index) => (
                        <MenuItem value={item!} key={index}>
                          {item}
                        </MenuItem>
                      ))}
                  </Select>
                  <Select
                    displayEmpty
                    IconComponent={ExpandMoreRoundedIcon}
                    startAdornment={<MergeTypeIcon />}
                    inputProps={{ "aria-label": "Without label" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    value={filterService}
                    onChange={(value) => setFilterService(value.target.value)}
                  >
                    <MenuItem value={""}>{t("menu:item.service")}</MenuItem>
                    {tasks
                      .map((item) => item.serviceName)
                      .filter(Distinct)
                      .map((item, index) => (
                        <MenuItem value={item!} key={index}>
                          {item}
                        </MenuItem>
                      ))}
                  </Select>
                  <Select
                    displayEmpty
                    IconComponent={ExpandMoreRoundedIcon}
                    startAdornment={<AssignmentIcon />}
                    inputProps={{ "aria-label": "Without label" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    value={filterTask}
                    onChange={(value) => setFilterTask(value.target.value)}
                  >
                    <MenuItem value={""}>{t("common:task")}</MenuItem>
                    {tasks
                      .map((item) => item.taskName)
                      .filter(Distinct)
                      .map((item, index) => (
                        <MenuItem value={item!} key={index}>
                          {item}
                        </MenuItem>
                      ))}
                  </Select>
                  <Select
                    displayEmpty
                    IconComponent={ExpandMoreRoundedIcon}
                    startAdornment={<CalendarMonth />}
                    inputProps={{ "aria-label": "Without label" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    value={filterDate}
                    onChange={(value, c) => setFilterDate(value.target.value)}
                  >
                    <MenuItem value={""}>{t("common:date")}</MenuItem>
                    <MenuItem value="Today" key={"today"}>
                      {t("menu:item.today")}
                    </MenuItem>
                    <MenuItem value="Tomorrow" key={"tmr"}>
                      {t("menu:item.tomorrow")}
                    </MenuItem>
                  </Select>
                  <Select
                    displayEmpty
                    IconComponent={ExpandMoreRoundedIcon}
                    startAdornment={<LowPriorityIcon />}
                    inputProps={{ "aria-label": "Without label" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    value={filterOrderBy}
                    onChange={(value, c) => setFilterOrderBy(value.target.value)}
                  >
                    <MenuItem value={""}>{t("menu:item.orderBy")}</MenuItem>
                    <MenuItem value="DueDate" key={"dd"}>
                      {t("common:dueDate")}
                    </MenuItem>
                    <MenuItem value="CreationDate" key={"cd"}>
                      {t("common:creationDateLowerCase")}
                    </MenuItem>
                  </Select>
                  <FormGroup sx={{ pl: 3 }}>
                    <FormControlLabel
                      control={<Checkbox value={onlyMine} onClick={() => setOnlyMine((prev) => !prev)} />}
                      label={<Typography variant="body2">{t("field:label.showOnlyMyTasks")}</Typography>}
                    />
                  </FormGroup>
                </ListPageHeaderGroupActions>
                <ListPageHeaderRight>
                  <OutlinedInput
                    label={t("common:search")}
                    placeholder={t("common:search")}
                    value={filterText}
                    onChange={(value) => setFilterText(value.target.value.toLowerCase())}
                    endAdornment={
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    }
                    sx={{ mr: "30px" }}
                  />
                </ListPageHeaderRight>
              </ListPageHeader>

              <Grid
                container
                spacing={3}
                pb={3}
                justifyContent="space-between"
                display="flex"
                flex={1}
                position="relative"
              >
                {statuses.map((status) => (
                  <Grid item md={2.4} lg={2.4} key={`grid${status}`}>
                    <Droppable droppableId={status}>
                      {(provided, snapshot) => (
                        <Stack {...provided.droppableProps} ref={provided.innerRef} sx={{ height: "100%", mt: 6 }}>
                          <Box
                            width={"100%"}
                            display={"flex"}
                            flexDirection={"row"}
                            position={"fixed"}
                            sx={{
                              backgroundColor: theme.palette.background.default,
                              p: 1.5,
                              top: "200px",
                              zIndex: 800,
                            }}
                          >
                            <Typography variant="body1">{status}</Typography>
                          </Box>
                          {taskGroups
                            .find((x) => x.status === status)
                            ?.tasks?.filter(
                              (x) =>
                                (filterText === "" ||
                                  x.titleName?.includesAllWords(filterText) ||
                                  x.taskName?.includesAllWords(filterText) ||
                                  x.customerName?.includesAllWords(filterText)) &&
                                (filterCustomer === "" || x.customerName === filterCustomer) &&
                                (filterService === "" || x.serviceName === filterService) &&
                                (filterTask === "" || x.taskName === filterTask) &&
                                (!onlyMine ||
                                  (onlyMine &&
                                    x.events?.some((e) => e.assignInfos && e.assignInfos.userId === user?.id))) &&
                                (filterDate === "" ||
                                  (filterDate === "Today" &&
                                    x.events?.some(
                                      (e) => e.assignInfos && moment(e.assignInfos.date).isSame(moment(), "day")
                                    )) ||
                                  (filterDate === "Tomorrow" &&
                                    x.events?.some(
                                      (e) =>
                                        e.assignInfos &&
                                        moment(e.assignInfos.date).isSame(moment().add(1, "day"), "day")
                                    )))
                            )
                            .sort((a, b) => {
                              if (filterOrderBy === "") return 0;
                              if (filterOrderBy === "DueDate" && a.orderDueDate && b.orderDueDate)
                                return moment(a.orderDueDate) > moment(b.orderDueDate) ? 1 : -1;
                              if (filterOrderBy === "CreationDate" && a.creationInfos?.date && b.creationInfos?.date)
                                return moment(a.creationInfos.date) < moment(b.creationInfos.date) ? 1 : -1;

                              return 1;
                            })
                            .map((task, index) => (
                              <Draggable draggableId={`task${task.id}`} index={index} key={`keytask${task.id}`}>
                                {(provided, snapshot) => (
                                  <Box
                                    sx={{ marginTop: index === 0 ? 8 : 0 }}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <KanbanTaskCard
                                      task={task}
                                      onClick={handleOpenPanel}
                                      users={viewableUsers}
                                      resources={resources}
                                    />
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                        </Stack>
                      )}
                    </Droppable>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </DragDropContext>
          <UserTaskPlanningPanel
            task={tasks.find((x) => x.id === selectedTaskId)}
            users={viewableUsers}
            resources={resources}
            open={panelOpen}
            onClose={togglePanelOpen}
          />
        </>
      )}
    </>
  );
}

export default PlanningKanban;
